/**
 * @file AcceptanceGate.jsx
 * @description Full-screen blocking modal shown when a logged-in user has
 *   not yet accepted the current version of the Terms of Service and/or
 *   Privacy Policy. Mirrors the BAA gate's "must scroll, then check the
 *   box, then accept" UX pattern.
 *
 *   Triggered by App.js when getAcceptanceStatus() reports either
 *   tosCurrent=false or ppCurrent=false. The gate covers the entire UI;
 *   user cannot dismiss, cannot navigate, cannot interact with the app
 *   until both acceptances are current (or until they log out).
 *
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module components/AcceptanceGate
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import { Btn } from "./UI";
import { ShieldIcon } from "./Icons";
import {
  TOS_VERSION, PP_VERSION, LEGAL_EFFECTIVE_DATE,
  TOS_SECTIONS, PP_SECTIONS,
} from "../lib/legalText";
import { recordBothAcceptances } from "../lib/legalAcceptanceService";
import { useModalViewport } from "../hooks/useModalViewport";

export default function AcceptanceGate({ userId, userEmail, onAccepted, onSignOut }) {
  const vp = useModalViewport();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("tos");
  const [tosScrolled, setTosScrolled] = useState(false);
  const [ppScrolled, setPpScrolled]   = useState(false);
  const [accepted, setAccepted]       = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState(null);
  const [ppPulse, setPpPulse]         = useState(false);
  const scrollRef = useRef(null);
  const autoAdvancedRef = useRef(false);

  // Reset scroll position when switching tabs
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = 0;
  }, [activeTab]);

  function handleScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
    if (nearBottom) {
      if (activeTab === "tos") {
        setTosScrolled(true);
        // Auto-advance to Privacy Policy once, with a brief pulse on the tab
        if (!autoAdvancedRef.current) {
          autoAdvancedRef.current = true;
          setPpPulse(true);
          setTimeout(() => {
            setActiveTab("pp");
            setPpPulse(false);
          }, 700);
        }
      } else {
        setPpScrolled(true);
      }
    }
  }

  const bothScrolled = tosScrolled && ppScrolled;
  const canAccept    = bothScrolled && accepted && !submitting;

  async function handleAccept() {
    if (!canAccept) return;
    setSubmitting(true);
    setError(null);
    const result = await recordBothAcceptances(userId);
    if (!result.ok) {
      setError(result.error || t("legal.gate.errorFallback"));
      setSubmitting(false);
      return;
    }
    onAccepted?.();
  }

  const sections = activeTab === "tos" ? TOS_SECTIONS : PP_SECTIONS;
  const version  = activeTab === "tos" ? TOS_VERSION  : PP_VERSION;
  const title    = activeTab === "tos" ? t("legal.termsOfService") : t("legal.privacyPolicy");

  // Scroll hint: which doc(s) still need scrolling
  const scrollHint = !tosScrolled && !ppScrolled
    ? t("legal.gate.scrollBothDocs")
    : !tosScrolled
    ? t("legal.gate.scrollTosDocs")
    : t("legal.gate.scrollPpDocs");

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(10, 8, 30, 0.78)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "20px 16px",
      backdropFilter: "blur(4px)",
    }}>
      <div style={{
        width: "100%", maxWidth: 760,
        background: T.surface,
        borderRadius: 16,
        border: `1px solid ${T.border}`,
        boxShadow: "0 24px 80px rgba(0,0,0,0.36)",
        display: "flex", flexDirection: "column",
        maxHeight: vp.maxH(1, 40),
        overflow: "hidden",
      }}>

        {/* Header */}
        <div style={{
          padding: "22px 28px 16px",
          borderBottom: `1px solid ${T.border}`,
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, flexShrink: 0,
              background: T.blueLight,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <ShieldIcon size={20} color={T.blue} />
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: T.text, letterSpacing: "-0.02em" }}>
                {t("legal.gate.title")}
              </div>
              <div style={{ fontSize: 12, color: T.textLight, marginTop: 1 }}>
                {t("legal.gate.description")}
              </div>
            </div>
          </div>

          {/* Tab switcher — two clearly-distinct, colorful steps so users see
              there are TWO documents to read and how far they've gotten. The
              step badge counts 1→2 and flips to a green ✓ once that doc has
              been scrolled; the active doc is a solid-blue pill. */}
          <div style={{
            display: "flex", gap: 6,
            background: T.surfaceAlt,
            borderRadius: 10, padding: 4,
            marginTop: 12,
          }}>
            {[
              ["tos", t("legal.termsOfService"), tosScrolled, "1"],
              ["pp",  t("legal.privacyPolicy"),  ppScrolled,  "2"],
            ].map(([key, label, scrolled, step]) => {
              const isActive = key === activeTab;
              const isPulsing = key === "pp" && ppPulse;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  style={{
                    flex: 1, padding: "11px 12px", borderRadius: 8,
                    fontSize: 13, fontWeight: isActive ? 800 : 600,
                    fontFamily: "'Inter', sans-serif",
                    border: "none", cursor: "pointer",
                    background: isActive ? T.blue : scrolled ? `${T.green}14` : T.surface,
                    color: isActive ? "#fff" : scrolled ? T.green : T.textMid,
                    boxShadow: isActive
                      ? `0 2px 10px ${T.blue}55`
                      : isPulsing ? `0 0 0 3px ${T.blue}33` : "none",
                    outline: isPulsing ? `2px solid ${T.blue}` : "none",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    transition: "all 0.2s ease",
                  }}
                >
                  <span style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                    fontSize: 11, fontWeight: 800,
                    background: isActive ? "rgba(255,255,255,0.25)" : scrolled ? T.green : T.border,
                    color: isActive ? "#fff" : scrolled ? "#fff" : T.textLight,
                  }}>
                    {scrolled ? "✓" : step}
                  </span>
                  {label}
                  {isPulsing && <span style={{ fontSize: 12 }}>→</span>}
                </button>
              );
            })}
          </div>

          <div style={{ marginTop: 10, fontSize: 11, color: T.textLight }}>
            {t("legal.gate.versionLine", { title, version, date: LEGAL_EFFECTIVE_DATE })}
          </div>
        </div>

        {/* Scrollable doc body */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          tabIndex={0}
          style={{
            flex: 1, overflowY: "auto",
            padding: "20px 28px",
            outline: "none", lineHeight: 1.75,
          }}
        >
          {sections.map((section) => (
            <div key={section.id} style={{ marginBottom: 22 }}>
              {section.heading && (
                <div style={{
                  fontSize: 13, fontWeight: 800, color: T.text,
                  letterSpacing: "-0.01em",
                  paddingBottom: 6,
                  borderBottom: `2px solid ${T.blueLight}`,
                  marginBottom: 10,
                }}>
                  {section.heading}
                </div>
              )}
              <div style={{
                fontSize: 13, color: T.textMid,
                whiteSpace: "pre-line", lineHeight: 1.75,
              }}>
                {section.body}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          flexShrink: 0,
          padding: "14px 28px 18px",
          borderTop: `1px solid ${T.border}`,
          background: T.surface,
        }}>
          {!bothScrolled && (
            <div style={{
              marginBottom: 12,
              padding: "8px 14px",
              background: `${T.yellow}18`,
              border: `1px solid ${T.yellow}40`,
              borderRadius: 8,
              fontSize: 12, color: "#92400E",
            }}>
              {t("legal.gate.scrollHint", { context: scrollHint })}
            </div>
          )}

          {error && (
            <div style={{
              marginBottom: 12, padding: "8px 14px",
              background: `${T.red}18`, border: `1px solid ${T.red}55`,
              borderRadius: 8, fontSize: 12, color: T.red,
            }}>
              {error}
            </div>
          )}

          <label style={{
            display: "flex", alignItems: "flex-start", gap: 10,
            cursor: bothScrolled ? "pointer" : "not-allowed",
            opacity: bothScrolled ? 1 : 0.45,
            marginBottom: 14,
          }}>
            <input
              type="checkbox"
              checked={accepted}
              disabled={!bothScrolled}
              onChange={e => setAccepted(e.target.checked)}
              style={{ marginTop: 2, width: 16, height: 16, accentColor: T.blue, cursor: bothScrolled ? "pointer" : "not-allowed" }}
            />
            <span style={{ fontSize: 13, color: T.text, lineHeight: 1.55 }}>
              {t("legal.gate.checkboxLabel", { tosVersion: TOS_VERSION, ppVersion: PP_VERSION, date: LEGAL_EFFECTIVE_DATE })}
            </span>
          </label>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button
              onClick={onSignOut}
              disabled={submitting}
              style={{
                padding: "9px 18px", background: "transparent",
                border: `1px solid ${T.border}`, borderRadius: 8,
                fontSize: 13, fontWeight: 600, color: T.textMid,
                cursor: "pointer", fontFamily: "'Inter', sans-serif",
              }}
            >
              {t("legal.gate.signOut")}
            </button>
            <Btn
              onClick={handleAccept}
              disabled={!canAccept}
              style={{ padding: "9px 24px", fontSize: 13, opacity: canAccept ? 1 : 0.45 }}
            >
              {submitting ? t("common.saving") : t("legal.gate.acceptButton")}
            </Btn>
          </div>

          <div style={{ marginTop: 10, fontSize: 10, color: T.textXLight, textAlign: "center" }}>
            {t("legal.gate.auditNotice")}
            {userEmail ? ` · ${userEmail}` : ""}
          </div>
        </div>
      </div>
    </div>
  );
}
